<template>
  <Dialog 
    :visible="dialogVisible" 
    @update:visible="dialogVisible = $event"
    :style="{ width: '500px' }" 
    header="Edit User" 
    :modal="true"
    :closable="true"
    :dismissableMask="false"
    class="edit-user-dialog"
  >
    <div class="p-fluid p-4">
      <div class="field mb-4">
        <label for="role" class="block mb-2 font-medium text-gray-700">Role *</label>
        <Dropdown 
          id="role"
          v-model="userForm.role_id" 
          :options="roles" 
          optionLabel="name" 
          optionValue="id"
          placeholder="Select a role"
          :class="{'p-invalid': v$.role_id.$invalid && v$.role_id.$dirty}"
          @blur="v$.role_id.$touch()"
          class="w-full"
          :loading="rolesLoading"
        />
        <small v-if="v$.role_id.$error" class="p-error mt-1 block text-red-500">
          {{ v$.role_id.$errors[0].$message }}
        </small>
      </div>

      <div class="field mb-4">
        <label for="firstName" class="block mb-2 font-medium text-gray-700">First Name *</label>
        <InputText 
          id="firstName" 
          v-model="userForm.first_name" 
          :class="{'p-invalid': v$.first_name.$invalid && v$.first_name.$dirty}"
          @blur="v$.first_name.$touch()"
          placeholder="Enter first name"
          class="p-3 border border-gray-300 w-full"
        />
        <small v-if="v$.first_name.$error" class="p-error mt-1 block text-red-500">
          {{ v$.first_name.$errors[0].$message }}
        </small>
      </div>

      <div class="field mb-4">
        <label for="lastName" class="block mb-2 font-medium text-gray-700">Last Name *</label>
        <InputText 
          id="lastName" 
          v-model="userForm.last_name" 
          :class="{'p-invalid': v$.last_name.$invalid && v$.last_name.$dirty}"
          @blur="v$.last_name.$touch()"
          placeholder="Enter last name"
          class="p-3 border border-gray-300 w-full"
        />
        <small v-if="v$.last_name.$error" class="p-error mt-1 block text-red-500">
          {{ v$.last_name.$errors[0].$message }}
        </small>
      </div>

      <div class="field mb-4">
        <label for="email" class="block mb-2 font-medium text-gray-700">Email *</label>
        <InputText 
          id="email" 
          v-model="userForm.email" 
          :class="{'p-invalid': v$.email.$invalid && v$.email.$dirty}"
          @blur="v$.email.$touch()"
          placeholder="Enter email address"
          class="p-3 border border-gray-300 w-full"
        />
        <small v-if="v$.email.$error" class="p-error mt-1 block text-red-500">
          {{ v$.email.$errors[0].$message }}
        </small>
      </div>

      <div class="field mb-4">
        <label for="phoneNumber" class="block mb-2 font-medium text-gray-700">
          Mobile Phone Number (with country code)
        </label>
        <InputText 
          id="phoneNumber" 
          v-model="userForm.phone_number" 
          placeholder="e.g. +44 7700 900123 (mobile numbers only)"
          class="p-3 border border-gray-300 w-full"
        />
        <small class="text-gray-500 mt-1 block">
          Include country code (e.g. +44 for UK). Mobile numbers only, not landlines.
        </small>
      </div>
    </div>
    <template #footer>
      <div class="flex justify-end gap-3 p-3">
        <Button 
          label="Cancel" 
          icon="pi pi-times" 
          class="p-button-text py-3 px-6" 
          @click="closeDialog" 
        />
        <Button 
          label="Update User" 
          icon="pi pi-check" 
          class="bg-blue-900 hover:bg-blue-800 text-white border-blue-900 py-3 px-6" 
          @click="handleSubmit" 
          :loading="loading"
          :disabled="loading || v$.$invalid"
        />
      </div>
    </template>
  </Dialog>
</template>

<script setup>
import { ref, reactive, watch } from 'vue'
import { useVuelidate } from '@vuelidate/core'
import { required, email } from '@vuelidate/validators'
import { useToast } from 'vue-toastification'
import axios from 'axios'

// PrimeVue components
import Button from 'primevue/button'
import InputText from 'primevue/inputtext'
import Dropdown from 'primevue/dropdown'
import Dialog from 'primevue/dialog'

const props = defineProps({
  tenant: {
    type: String,
    required: true
  },
  user: {
    type: Object,
    default: null
  },
  visible: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['update:visible', 'userUpdated'])

// Toast for notifications
const toast = useToast()

// Dialog state
const dialogVisible = ref(false)
const loading = ref(false)
const rolesLoading = ref(false)

// Roles data
const roles = ref([])

// Form data
const userForm = reactive({
  role_id: null,
  first_name: '',
  last_name: '',
  email: '',
  phone_number: ''
})

// Validation rules
const rules = {
  role_id: { required },
  first_name: { required },
  last_name: { required },
  email: { required, email }
}

const v$ = useVuelidate(rules, userForm)

// Watch for prop changes
watch(() => props.visible, (newVal) => {
  dialogVisible.value = newVal
  if (newVal) {
    fetchRoles()
    populateForm()
  }
})

watch(() => dialogVisible.value, (newVal) => {
  emit('update:visible', newVal)
})

// Fetch roles from API
const fetchRoles = async () => {
  rolesLoading.value = true
  try {
    const response = await axios.get('/api/v1/roles_proxy', {
      params: { tenant: props.tenant }
    })
    
    if (response.data && response.data.data) {
      roles.value = response.data.data
    } else {
      roles.value = response.data || []
    }
  } catch (error) {
    console.error('Error fetching roles:', error)
    toast.error('Failed to load roles')
    roles.value = []
  } finally {
    rolesLoading.value = false
  }
}

// Populate form with user data
const populateForm = () => {
  if (props.user) {
    userForm.role_id = props.user.role_id || null
    userForm.first_name = props.user.first_name || ''
    userForm.last_name = props.user.last_name || ''
    userForm.email = props.user.email || ''
    userForm.phone_number = props.user.phone_number || ''
  }
}

// Event handlers
const closeDialog = () => {
  dialogVisible.value = false
  resetForm()
}

// Handle form submission
const handleSubmit = async () => {
  const isValid = await v$.value.$validate()
  if (!isValid) return
  
  loading.value = true
  
  try {
    const response = await axios.patch(`/api/v1/users_proxy/${props.user.id}`, {
      role_id: userForm.role_id,
      email: userForm.email,
      first_name: userForm.first_name,
      last_name: userForm.last_name,
      phone_number: userForm.phone_number,
      tenant: props.tenant
    })
    
    if (response.status === 200) {
      toast.success('User updated successfully!')
      closeDialog()
      emit('userUpdated', response.data)
    } else {
      toast.error('Failed to update user')
    }
  } catch (error) {
    console.error('Error updating user:', error)
    if (error.response && error.response.data) {
      const errorData = error.response.data
      if (errorData.error) {
        toast.error(errorData.error)
      } else if (errorData.details) {
        toast.error(errorData.details)
      } else {
        toast.error('Failed to update user')
      }
    } else {
      toast.error('An error occurred while updating the user')
    }
  } finally {
    loading.value = false
  }
}

// Reset form
const resetForm = () => {
  userForm.role_id = null
  userForm.first_name = ''
  userForm.last_name = ''
  userForm.email = ''
  userForm.phone_number = ''
  v$.value.$reset()
}
</script>

<style scoped>
/* Dialog styling */
:deep(.edit-user-dialog .p-dialog-header) {
  padding: 1.5rem;
  border-bottom: 1px solid #e5e7eb;
}

:deep(.edit-user-dialog .p-dialog-content) {
  padding: 0;
}

:deep(.edit-user-dialog .p-dialog-footer) {
  padding: 0;
  border-top: 1px solid #e5e7eb;
}

.p-button {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.p-inputtext {
  border: 1px solid #ced4da !important;
  border-radius: 4px !important;
  transition: border-color 0.2s, box-shadow 0.2s !important;
  width: 100% !important;
  display: block !important;
}

.p-inputtext:hover {
  border-color: #2563eb !important;
}

.p-inputtext:focus {
  border-color: #2563eb !important;
  box-shadow: 0 0 0 0.2rem rgba(37, 99, 235, 0.25) !important;
  outline: 0 none !important;
}

.p-invalid {
  border-color: #ef4444 !important;
}

.p-error {
  color: #ef4444 !important;
  margin-top: 0.25rem !important;
  font-size: 0.875rem !important;
  display: block !important;
  font-style: italic !important;
}

.field {
  margin-bottom: 1rem;
}

label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: #374151;
}
</style>
